import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatTableModule } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { HelpRequestsService } from '../services/help-requests.service';
import { HelpRequestDTO } from '../model/help-request-dto';
import { HelpRequestFileDTO } from '../model/help-request-file-dto';
import { HelpRequestMessageDTO } from '../model/help-request-message-dto';
import { HelpRequestStatusAction } from '../model/help-request-status-request-dto';
import { ModalComponent } from '../utils/modal/modal.component';

@Component({
  selector: 'app-help-requests',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatChipsModule,
    MatDialogModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatSnackBarModule
  ],
  templateUrl: './help-requests.component.html',
  styleUrls: ['./help-requests.component.scss']
})
export class HelpRequestsComponent implements OnInit {
  displayedColumns: string[] = ['caseCode', 'originType', 'createdAt', 'statusName', 'chatPreview', 'actions'];
  cases: HelpRequestDTO[] = [];
  loading: boolean = false;
  error: string | null = null;

  // Paginación
  currentPage: number = 0;
  pageSize: number = 10;
  totalElements: number = 0;
  totalPages: number = 0;

  // Caso seleccionado para ver detalle/archivos/conversación
  selectedCaseWithFiles: HelpRequestDTO | null = null;
  loadingFiles: boolean = false;

  // Caja de respuesta
  replyMessage: string = '';
  sendingReply: boolean = false;

  // Cambio de estado (cerrar/reabrir)
  updatingStatus: boolean = false;

  constructor(
    private helpRequestsService: HelpRequestsService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.loadCases();
  }

  loadCases(): void {
    this.loading = true;
    this.error = null;

    // Endpoint 1: Lista paginada SIN archivos
    this.helpRequestsService.getHelpRequests(this.currentPage, this.pageSize)
      .subscribe({
        next: (response) => {
          if (response.statusCode === '000' && response.data) {
            this.cases = response.data.content;
            this.totalElements = response.data.totalElements;
            this.totalPages = response.data.totalPages;
            // Nota: files en cada caso viene vacío [] en este endpoint
          } else {
            this.error = response.message || 'Error al cargar los casos';
          }
          this.loading = false;
        },
        error: (err) => {
          this.error = 'Error al cargar los casos: ' + err.message;
          this.loading = false;
        }
      });
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadCases();
  }

  // Endpoint 2: Obtener detalles, conversación en vivo y archivos de un caso específico
  viewCaseDetails(caseId: string, keepModalOpen: boolean = false): void {
    this.loadingFiles = true;
    if (!keepModalOpen) {
      this.selectedCaseWithFiles = null;
    }
    this.error = null;

    this.helpRequestsService.getHelpRequestById(caseId)
      .subscribe({
        next: (response) => {
          if (response.statusCode === '000' && response.data) {
            this.selectedCaseWithFiles = response.data;
            // Ahora el caso tiene files[] con los archivos reales y messages[] con la conversación en vivo
          } else {
            this.error = response.message || 'Error al cargar los detalles del caso';
          }
          this.loadingFiles = false;
        },
        error: (err) => {
          this.error = 'Error al cargar los detalles: ' + err.message;
          this.loadingFiles = false;
        }
      });
  }

  openFile(file: HelpRequestFileDTO): void {
    if (file.preSignedUrl) {
      window.open(file.preSignedUrl, '_blank');
    }
  }

  closeFilesModal(): void {
    this.selectedCaseWithFiles = null;
    this.replyMessage = '';
  }

  getOriginTypeColor(originType: string): string {
    switch (originType) {
      case 'CHAT': return 'primary';
      case 'EMAIL': return 'accent';
      case 'WEB': return 'warn';
      case 'POST': return 'primary';
      case 'JOB': return 'accent';
      default: return 'primary';
    }
  }

  /**
   * Devuelve los mensajes de la conversación a pintar: prioriza la conversación
   * en vivo (`messages`) y usa el parseo de `chatPreview` solo como fallback
   * cuando el backend no la incluye.
   */
  getConversationMessages(caseItem: HelpRequestDTO): HelpRequestMessageDTO[] {
    if (caseItem.messages && caseItem.messages.length > 0) {
      return caseItem.messages;
    }
    return this.parseChatMessages(caseItem.chatPreview);
  }

  parseChatMessages(chatPreview: string): HelpRequestMessageDTO[] {
    try {
      const messages = JSON.parse(chatPreview);
      return Array.isArray(messages) ? messages : [];
    } catch (error) {
      console.error('Error parsing chat messages:', error);
      return [];
    }
  }

  isUserMessage(sender: string): boolean {
    return sender === 'USER';
  }

  isOperatorMessage(sender: string): boolean {
    return sender === 'OPERATOR' || sender === 'ADMIN';
  }

  isAssistantMessage(sender: string): boolean {
    return !this.isUserMessage(sender) && !this.isOperatorMessage(sender);
  }

  getSenderLabel(sender: string): string {
    if (this.isUserMessage(sender)) return 'Usuario';
    if (this.isOperatorMessage(sender)) return 'Operador';
    return 'Asistente';
  }

  formatTimestamp(timestamp: number): string {
    return new Date(timestamp).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getFileIcon(extension: string): string {
    const ext = extension.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) {
      return 'image';
    } else if (['pdf'].includes(ext)) {
      return 'picture_as_pdf';
    } else if (['doc', 'docx'].includes(ext)) {
      return 'description';
    } else if (['xls', 'xlsx'].includes(ext)) {
      return 'table_chart';
    } else if (['zip', 'rar', '7z'].includes(ext)) {
      return 'folder_zip';
    } else {
      return 'insert_drive_file';
    }
  }

  /**
   * Clase CSS a aplicar al chip de estado según el nombre de estado.
   * SOLICITADO = acento, ABIERTO = azul/info, CERRADO = gris, EN CHAT = neutro.
   */
  getStatusClass(statusName: string | undefined | null): string {
    const normalized = (statusName || '').trim().toUpperCase();
    switch (normalized) {
      case 'SOLICITADO': return 'status-solicitado';
      case 'ABIERTO': return 'status-abierto';
      case 'CERRADO': return 'status-cerrado';
      case 'EN CHAT': return 'status-en-chat';
      default: return 'status-default';
    }
  }

  trackByMessage(index: number, message: HelpRequestMessageDTO): string {
    return `${message.timestamp}-${index}`;
  }

  isCaseClosed(caseItem: HelpRequestDTO | null): boolean {
    return (caseItem?.statusName || '').trim().toUpperCase() === 'CERRADO';
  }

  /**
   * Envía la respuesta del operador al caso seleccionado y recarga el detalle
   * para reflejar el mensaje ya en la conversación.
   */
  sendReply(): void {
    if (!this.selectedCaseWithFiles || !this.replyMessage.trim() || this.sendingReply) {
      return;
    }
    if (this.isCaseClosed(this.selectedCaseWithFiles)) {
      return;
    }

    const caseId = this.selectedCaseWithFiles.id;
    this.sendingReply = true;

    this.helpRequestsService.replyToCase(caseId, this.replyMessage.trim())
      .subscribe({
        next: (response) => {
          this.sendingReply = false;
          if (response.statusCode === '000') {
            this.replyMessage = '';
            this.snackBar.open('Respuesta enviada correctamente', 'Cerrar', {
              duration: 3000,
              panelClass: ['success-snackbar']
            });
            this.viewCaseDetails(caseId, true);
          } else {
            this.snackBar.open(response.message || 'Error al enviar la respuesta', 'Cerrar', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          }
        },
        error: (err) => {
          this.sendingReply = false;
          this.snackBar.open('Error al enviar la respuesta: ' + err.message, 'Cerrar', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        }
      });
  }

  /**
   * Pide confirmación y cierra el caso seleccionado.
   */
  confirmCloseCase(): void {
    if (!this.selectedCaseWithFiles) return;

    this.dialog.open(ModalComponent, {
      width: '550px',
      disableClose: true,
      data: {
        title: 'Confirmación',
        message: '¿Está seguro que desea cerrar este caso?',
        actions: [
          { label: 'Aceptar', callback: () => this.updateCaseStatus('CLOSE') },
          { label: 'Cancelar', callback: () => {} }
        ]
      }
    });
  }

  /**
   * Pide confirmación y reabre el caso seleccionado.
   */
  confirmReopenCase(): void {
    if (!this.selectedCaseWithFiles) return;

    this.dialog.open(ModalComponent, {
      width: '550px',
      disableClose: true,
      data: {
        title: 'Confirmación',
        message: '¿Está seguro que desea reabrir este caso?',
        actions: [
          { label: 'Aceptar', callback: () => this.updateCaseStatus('REOPEN') },
          { label: 'Cancelar', callback: () => {} }
        ]
      }
    });
  }

  private updateCaseStatus(action: HelpRequestStatusAction): void {
    if (!this.selectedCaseWithFiles || this.updatingStatus) return;

    const caseId = this.selectedCaseWithFiles.id;
    this.updatingStatus = true;

    this.helpRequestsService.updateCaseStatus(caseId, action)
      .subscribe({
        next: (response) => {
          this.updatingStatus = false;
          if (response.statusCode === '000') {
            this.snackBar.open(
              action === 'CLOSE' ? 'Caso cerrado correctamente' : 'Caso reabierto correctamente',
              'Cerrar',
              { duration: 3000, panelClass: ['success-snackbar'] }
            );
            this.viewCaseDetails(caseId, true);
            this.loadCases();
          } else {
            this.snackBar.open(response.message || 'No fue posible actualizar el estado del caso', 'Cerrar', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          }
        },
        error: (err) => {
          this.updatingStatus = false;
          const message = err?.error?.message || err.message;
          this.snackBar.open('Error al actualizar el estado del caso: ' + message, 'Cerrar', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        }
      });
  }
}
